import React from "react";

const polarToCartesian = (cx, cy, radius, angle) => {
    const radians = ((angle - 90) * Math.PI) / 180;

    return {
        x: cx + radius * Math.cos(radians),
        y: cy + radius * Math.sin(radians),
    };
};

const describeArc = (cx, cy, radius, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
        "M",
        start.x,
        start.y,
        "A",
        radius,
        radius,
        0,
        largeArcFlag,
        0,
        end.x,
        end.y,
    ].join(" ");
};

const isMobile = window.innerWidth < 768;

export default function Gauge({
    value = 0,
    min = 0,
    max = 100,
    unit = "V",
    width = 180,
    segments = [],
}) {
    const size = width;
    const cx = size / 2;
    const cy = size / 2 + 10;

    const radius = size * 0.38;

    const startAngle = -135;
    const endAngle = 135;
    const sweep = endAngle - startAngle;

    const clamped = Math.min(Math.max(value, min), max);

    const percentage =
        (clamped - min) / (max - min);

    const needleAngle =
        startAngle + percentage * sweep;

    const majorTicks = 6;
    const minorTicks = 42;

    const createNeedle = () => {
        const tip = polarToCartesian(
            cx,
            cy,
            radius - 20,
            needleAngle
        );

        const left = polarToCartesian(
            cx,
            cy,
            8,
            needleAngle - 90
        );

        const right = polarToCartesian(
            cx,
            cy,
            8,
            needleAngle + 90
        );

        return `
      ${tip.x},${tip.y}
      ${left.x},${left.y}
      ${right.x},${right.y}
    `;
    };

    return (
      <div
        style={{
            width,
            //height: width,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            // border: "1px solid #999",
            // background: "#fff",
            // boxShadow:
            //     "inset 0 0 4px rgba(0,0,0,0.15)",
            paddingY: 4,
        }}
      >
        <svg
            style={{ display: "block" }}
            width={size}
            height={size - 10}
            viewBox={`0 0 ${size} ${size}`}
        >
          <defs>
              <linearGradient
                  id="bezel"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
              >
                  <stop
                      offset="0%"
                      stopColor="#f8f8f8"
                  />
                  <stop
                      offset="50%"
                      stopColor="#cfcfcf"
                  />
                  <stop
                      offset="100%"
                      stopColor="#f8f8f8"
                  />
              </linearGradient>
          </defs>

          {/* Outer metallic ring */}
          {/* <circle
              cx={cx}
              cy={cy}
              r={radius + 10}
              fill="none"
              stroke="url(#bezel)"
              strokeWidth="8"
          /> */}

          {/* Dial background */}
          <circle
              cx={cx}
              cy={cy}
              r={radius}
              fill="white"
              stroke="#ccc"
              strokeWidth="1"
          />

          {/* Color segments */}
          {segments.map((segment, index) => {
              const segStart =
                  startAngle +
                  ((segment.from - min) /
                      (max - min)) *
                  sweep;

              const segEnd =
                  startAngle +
                  ((segment.to - min) /
                      (max - min)) *
                  sweep;

              return (
                  <path
                      key={index}
                      d={describeArc(
                          cx,
                          cy,
                          radius - 4,
                          segStart,
                          segEnd
                      )}
                      stroke={segment.color}
                      strokeWidth="12"
                      fill="none"
                      strokeLinecap="round"
                  />
              );
          })}

          {/* Minor ticks */}
          {Array.from({
              length: minorTicks + 1,
          }).map((_, i) => {
              const angle =
                  startAngle +
                  (i / minorTicks) * sweep;

              const p1 =
                  polarToCartesian(
                      cx,
                      cy,
                      radius - 2,
                      angle
                  );

              const p2 =
                  polarToCartesian(
                      cx,
                      cy,
                      radius - 9,
                      angle
                  );

              return (
                  <line
                      key={i}
                      x1={p1.x}
                      y1={p1.y}
                      x2={p2.x}
                      y2={p2.y}
                      stroke="#444"
                      strokeWidth="1"
                  />
              );
          })}

          {/* Major ticks + labels */}
          {Array.from({
              length: majorTicks + 1,
          }).map((_, i) => {
              const angle =
                  startAngle +
                  (i / majorTicks) * sweep;

              const p1 =
                  polarToCartesian(
                      cx,
                      cy,
                      radius - 1,
                      angle
                  );

              const p2 =
                  polarToCartesian(
                      cx,
                      cy,
                      radius - 16,
                      angle
                  );

              const label =
                  polarToCartesian(
                      cx,
                      cy,
                      radius - 30,
                      angle
                  );

              const tickValue =
                  min +
                  ((max - min) /
                      majorTicks) *
                  i;

              return (
                  <g key={i}>
                      <line
                          x1={p1.x}
                          y1={p1.y}
                          x2={p2.x}
                          y2={p2.y}
                          stroke="#000"
                          strokeWidth="2"
                      />

                      <text
                          x={label.x}
                          y={label.y}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize={isMobile ? "8" : "11"}
                          fontWeight="bold"
                      >
                          {Math.round(
                              tickValue
                          )}
                      </text>
                  </g>
              );
          })}

          {/* Glass reflection */}
          <ellipse
              cx={cx}
              cy={cy - radius * 0.45}
              rx={radius * 0.75}
              ry={radius * 0.22}
              fill="rgba(255,255,255,0.25)"
          />

          {/* Needle */}
          <polygon
              points={createNeedle()}
              fill="#111"
              style={{
                  transition:
                      "all 0.4s ease",
              }}
          />

          {/* Hub */}
          <circle
              cx={cx}
              cy={cy}
              r="12"
              fill="#444"
          />

          <circle
              cx={cx}
              cy={cy}
              r="7"
              fill="#ddd"
          />

          <circle
              cx={cx}
              cy={cy}
              r="4"
              fill="#666"
          />
        </svg>

        <div
            style={{
                width: size,
                textAlign: "center",
                marginTop: 6,
                fontWeight: 600,
                fontSize: isMobile ? 16 : 18,
            }}
        >
            {isMobile ? "Avg." : "Average"} {unit} : {value.toFixed(1)}
        </div>
      </div>
  );
}